import React from "react";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import type { PaceWindow } from "../../../types/stationPerformance";

interface PaceWindowSummaryProps {
  window: PaceWindow;
  asOf: string;
  currentMonth: string;
  previousMonth: string;
  thresholdPercent: number;
}

/**
 * States exactly what was compared, before any verdict is shown.
 *
 * A watchlist that does not show its window is untrustworthy: the reader cannot tell whether "down 40%"
 * means a real fall or just an incomplete month measured against a complete one. Everything here exists
 * so the flags below can be believed: or discounted.
 */
const PaceWindowSummary: React.FC<PaceWindowSummaryProps> = ({
  window,
  asOf,
  currentMonth,
  previousMonth,
  thresholdPercent,
}) => {
  const noneElapsed = window.elapsedDays === 0;

  return (
    <div className="mb-3">
      {noneElapsed && (
        <Message
          className="mb-2 w-full"
          severity="info"
          text={`No complete day has elapsed in ${currentMonth} yet, so there is nothing to compare. Nothing is flagged — which is the point: comparing an empty month against a full one would flag the entire fleet.`}
        />
      )}

      {window.confidence === "LOW" && !noneElapsed && (
        <Message
          className="mb-2 w-full"
          severity="warn"
          text={`Only ${window.elapsedDays} complete day${window.elapsedDays === 1 ? "" : "s"} of ${currentMonth} so far — fewer than the ${window.minDaysForConfidence} this comparison needs to be meaningful. Treat these as early signals, not verdicts.`}
        />
      )}

      {window.windowClamped && (
        <Message
          className="mb-2 w-full"
          severity="info"
          text={`${previousMonth} was shorter than the elapsed period, so the comparison was clamped to its day ${window.clampedToDay}. Both sides cover ${window.comparisonDays} days.`}
        />
      )}

      <div className="surface-100 border-round p-3 text-sm flex flex-wrap gap-3 align-items-center">
        <span>
          Comparing <strong>{currentMonth}</strong> days 1–{window.elapsedDays} against{" "}
          <strong>{previousMonth}</strong> days 1–{window.comparisonDays}
        </span>
        <Tag
          value={window.confidence === "LOW" ? "low confidence" : "normal confidence"}
          severity={window.confidence === "LOW" ? "warning" : "success"}
        />
        <span className="text-color-secondary">as at {asOf}</span>
        <span className="text-color-secondary">
          flagging below {thresholdPercent.toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

export default PaceWindowSummary;
